import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="container page-container">
      <article className="page-card legal-page about-page">
        <header className="page-card-header">
          <p className="page-eyebrow">À propos</p>
          <h1>Une application québécoise, pensée pour les pathologistes</h1>
          <p className="page-lead">
            Medcodes est un outil indépendant, conçu au Québec, pour le quotidien
            réel de la pathologie et de la facturation RAMQ.
          </p>
        </header>

        <section>
          <h2>Qui sommes-nous</h2>
          <p>
            Medcodes est une application de préfacturation destinée aux
            pathologistes du Québec. Elle existe pour un besoin précis : enregistrer
            les cas réalisés, suivre les points, et produire un rapport
            clair, prêt à saisir sur le site de la RAMQ.
          </p>
        </section>

        <aside className="about-callout">
          <h2>Un flux de travail conçu par un pathologiste</h2>
          <p>
            Le déroulement de l'application a été développé par un pathologiste,
            pour des pathologistes. La saisie des cas, l'organisation des codes et
            le format du rapport ont été pensés à partir du travail réel.
          </p>
          <p>
            Le résultat : quelques gestes pour consigner un cas, puis un rapport
            automatisé spécifique à la RAMQ qui facilite la facturation, sans reformater
            manuellement un tableur en fin de période.
          </p>
        </aside>

        <section>
          <h2>Pourquoi Medcodes convient à la pathologie au Québec</h2>
          <div className="about-highlights">
            <div className="home-feature-item">
              <div className="home-feature-icon" aria-hidden="true">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div className="home-feature-content">
                <h3>Faite pour le Québec</h3>
                <p>
                  Codes de pathologie, points et rapport orienté RAMQ : l'outil
                  suit le cadre de facturation québécois, pas un modèle d'ailleurs.
                </p>
              </div>
            </div>
            <div className="home-feature-item">
              <div className="home-feature-icon" aria-hidden="true">
                <i className="fas fa-file-invoice"></i>
              </div>
              <div className="home-feature-content">
                <h3>Rapport prêt pour la RAMQ</h3>
                <p>
                  L'historique est présenté pour accélérer la saisie sur le site
                  de la RAMQ, plutôt que de reconstruire le rapport à chaque fois.
                </p>
              </div>
            </div>
            <div className="home-feature-item">
              <div className="home-feature-icon" aria-hidden="true">
                <i className="fas fa-mobile-alt"></i>
              </div>
              <div className="home-feature-content">
                <h3>Ordinateur, tablette et mobile</h3>
                <p>
                  L'application s'adapte à l'écran : au bureau, sur une tablette
                  au laboratoire, ou sur votre téléphone.
                </p>
              </div>
            </div>
            <div className="home-feature-item">
              <div className="home-feature-icon" aria-hidden="true">
                <i className="fas fa-history"></i>
              </div>
              <div className="home-feature-content">
                <h3>Historique toujours accessible</h3>
                <p>
                  Vos cas et vos points restent disponibles en tout temps, pour
                  consulter ou facturer quand vous en avez besoin.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Comment ça se passe au quotidien</h2>
          <ol className="about-steps">
            <li>
              <strong>Vous consignez vos cas au fur et à mesure.</strong>
              {' '}Les codes de pathologie s'ajoutent rapidement, pendant ou juste
              après la lecture, sans interrompre le rythme de travail.
            </li>
            <li>
              <strong>Vous gardez un historique clair.</strong>
              {' '}Les cas et les points restent accessibles, classés, prêts à
              consulter quand vient le temps de facturer.
            </li>
            <li>
              <strong>Vous obtenez un rapport au format RAMQ.</strong>
              {' '}Il est généré automatiquement selon le format de la RAMQ,
              pour faciliter la facturation par la suite.
            </li>
            <li>
              <strong>Vous suivez votre volume.</strong>
              {' '}Un tableau de bord résume les points par jour, semaine, mois
              et année, ainsi que par type de cas.
            </li>
          </ol>
        </section>

        <section className="about-faq" aria-labelledby="about-faq-title">
          <h2 id="about-faq-title">Questions fréquentes avant de créer un compte</h2>

          <h3>Est-ce vraiment adapté à la facturation RAMQ en pathologie?</h3>
          <p>
            Oui. Le flux de travail a été développé par un pathologiste, et le
            rapport est conçu pour la saisie sur le site de la RAMQ. Medcodes
            n'est pas un logiciel médical générique auquel on aurait ajouté
            quelques codes québécois.
          </p>

          <h3>Quelles informations dois-je entrer?</h3>
          <p>
            Uniquement ce qui sert à la préfacturation : votre compte (courriel,
            nom) et les codes de facturation des cas. Aucun renseignement
            personnel ou médical de patient n'est saisi dans l'application.
          </p>

          <h3>Où sont hébergées mes données?</h3>
          <p>
            Sur des serveurs situés au Canada. Medcodes ne vend ni ne loue vos
            renseignements. Les détails figurent dans la{' '}
            <Link to="/privacy">politique de confidentialité</Link>.
          </p>

          <h3>L'application est-elle gratuite?</h3>
          <p>
            Oui. La création d'un compte et l'utilisation de Medcodes sont
            gratuites.
          </p>

          <h3>Comment commencer?</h3>
          <p>
            Créez un compte avec votre courriel, confirmez-le, puis commencez à
            consigner vos cas. Si quelque chose n'est pas clair, écrivez-nous :
            une personne réelle répond.
          </p>
        </section>

        <footer className="legal-page-footer">
          <div className="about-cta">
            <Link to="/signup" className="btn btn-primary">
              Créer un compte
            </Link>
            <Link to="/contact" className="btn btn-outline-primary">
              Nous joindre
            </Link>
            <Link to="/" className="btn btn-outline-primary">
              Retour à l'accueil
            </Link>
          </div>
          <p className="about-cta-note">
            Une question avant de vous inscrire?{' '}
            <Link to="/contact">Remplissez le formulaire de contact</Link>.
          </p>
        </footer>
      </article>
    </div>
  );
};

export default About;
